/** @format */

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import Loader from "../../elements/Loader";
import Select from "react-select";
import Modal from "../../elements/Modal";

export default function PaymentAudit() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [hasChanges, setIsChanged] = useState(false);
  const [panditOptions, setPanditOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);

  const [manualPaymentData, setManualPaymentData] = useState({ paymentMethod: '', amount: '', paymentId: '' });

  // Versioning states
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [selectedVersionType, setSelectedVersionType] = useState("");
  const [versionLoading, setVersionLoading] = useState(false);

  const fetchAuditDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiService.get(`payment/audit/${bookingId}`);
      if (response && response.result) {
        setBooking(response.result);
        setEditValues({
          finalPrice: response.result.finalPrice,
          actualPrice: response.result.actualPrice,
          panditId: response.result.panditId,
          serviceId: response.result.serviceId,
          discountAmount: response.result.discountAmount,
        });
      }
    } catch (err) {
      toast.error("Failed to fetch payment audit details");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  const fetchOptions = useCallback(async () => {
    try {
      const [pandits, services] = await Promise.all([
        ApiService.get('user/role/pandit?dropdown=true'),
        ApiService.get('service/list')
      ]);
      if (pandits?.data) {
        setPanditOptions(pandits.data.map(p => ({ value: p.id, label: p.name })));
      }
      if (services?.data) {
        setServiceOptions(services.data.map(s => ({ value: s.id, label: s.title, price: s.price })));
      }
    } catch (err) {
      console.error("Error fetching options", err);
    }
  }, []);

  useEffect(() => {
    if (bookingId) {
      fetchAuditDetails();
      fetchOptions();
    }
  }, [bookingId, fetchAuditDetails, fetchOptions]);

  const handleEditChange = (field, value) => {
    const newValues = { ...editValues, [field]: value };
    setEditValues(newValues);
    
    // Simple change detection
    const changed = Object.keys(newValues).some(key => newValues[key] !== booking[key]);
    setIsChanged(changed);
  };

  const handleUpdateAuditData = async () => {
    setLoading(true);
    try {
      await ApiService.post('payment/update-audit', {
        bookingId,
        data: editValues
      });
      toast.success("Details updated. You can now regenerate the invoice.");
      await fetchAuditDetails();
      setIsEditing(false);
      setIsChanged(false);
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async (stage) => {
    const existingUrl = stage === 'proforma' ? booking?.proformaInvoiceUrl
      : stage === 'final' ? booking?.finalInvoiceUrl
      : booking?.panditInvoiceUrl;
    const action = existingUrl ? 'regenerate' : 'generate';
    const warning = existingUrl ? ' This will create a new version in S3.' : '';
    if (!window.confirm(`Are you sure you want to ${action} the ${stage} invoice?${warning}`)) {
      return;
    }
    setLoading(true);
    try {
      const response = await ApiService.post('booking/generate-invoice', { bookingId, stage, force: true });
      if (response?.msg === 'skipped') {
        toast.info(response.message || 'Invoice generation skipped.');
      } else {
        toast.success(`✅ ${stage} Invoice ${action}d successfully!`);
      }
      await fetchAuditDetails();
    } catch (err) {
      const message = err.response?.data?.error || `${action.charAt(0).toUpperCase() + action.slice(1)}ion failed`;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvoice = async (stage) => {
    if (!window.confirm(`Are you sure you want to send the ${stage} invoice to the client? This will trigger email and WhatsApp notifications.`)) {
      return;
    }
    setLoading(true);
    try {
      await ApiService.post('booking/send-invoice', { bookingId, stage });
      toast.success(`✉️ ${stage} Invoice sent to client!`);
    } catch (err) {
      toast.error("Failed to send invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleShowVersions = async (type) => {
    setVersionLoading(true);
    setSelectedVersionType(type);
    setVersionModalOpen(true);
    try {
      const response = await ApiService.get(`payment/audit/${bookingId}/versions?type=${type}`);
      if (response && response.result) {
        setSelectedVersions(response.result);
      }
    } catch (err) {
      toast.error("Failed to fetch invoice versions");
      setVersionModalOpen(false);
    } finally {
      setVersionLoading(false);
    }
  };

  const requestRemainingPayment = async () => {
    setLoading(true);
    try {
      await ApiService.post(`booking/request-remaining-payment/${bookingId}`);
      toast.success("Remaining payment link sent successfully!");
      await fetchAuditDetails();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to send remaining payment link");
    } finally {
      setLoading(false);
    }
  };

  const handleAddManualPayment = async () => {
    const { paymentMethod, amount, paymentId } = manualPaymentData;
    if (!paymentMethod || !amount) {
      toast.error('Payment method and amount are required');
      return;
    }
    try {
      setLoading(true);
      await ApiService.post('booking/payment-history', {
        bookingId,
        paymentMethod,
        amount: parseFloat(amount),
        paymentId: paymentId || null,
        status: 'paid'
      });
      toast.success('Payment recorded successfully');
      setManualPaymentData({ paymentMethod: '', amount: '', paymentId: '' });
      await fetchAuditDetails();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const deletePaymentRecord = async (paymentId) => {
    if (window.confirm('Are you sure you want to delete this payment record? This action cannot be undone.')) {
      setLoading(true);
      try {
        await ApiService.delete(`booking/payment-history/${paymentId}`);
        toast.success('Payment record deleted successfully');
        await fetchAuditDetails(); // Refresh all audit data
      } catch (error) {
        console.error('Error deleting payment record:', error);
        toast.error('Failed to delete payment record');
      } finally {
        setLoading(false);
      }
    }
  };

  const convertToIST = (utcDate) => {
    if (!utcDate) return "NA";
    const date = new Date(utcDate);
    return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  };

  if (loading && !booking) return <Loader />;

  // Calculate dynamic totals for the audit summary
  const totalPaidToDate = booking?.paymentHistory
    ?.filter(p => p.status === 'success' || p.status === 'paid' || p.status === 'captured')
    .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;
  
  const totalBookingAmount = parseFloat(booking?.finalPrice || booking?.actualPrice || 0);
  const balanceDue = Math.max(0, totalBookingAmount - totalPaidToDate);

  return (
    <div className="container-fluid">
      <div className="row page-titles mx-0">
        <div className="col-sm-6 p-md-0">
          <div className="welcome-text">
            <h4>Payment Audit & Invoicing</h4>
            <p className="mb-0">Booking ID: {booking?.bookingId}</p>
          </div>
        </div>
        <div className="col-sm-6 p-md-0 justify-content-sm-end mt-2 mt-sm-0 d-flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => setIsEditing(!isEditing)}>
            {isEditing ? 'Cancel Editing' : 'Edit Invoice Data'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/payments')}>
            <i className="fa fa-arrow-left me-1"></i> Back to List
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-3 col-lg-4">
          <div className="card">
            <div className="card-header border-0 pb-0">
              <h4 className="card-title">Customer & Service</h4>
            </div>
            <div className="card-body">
              {isEditing ? (
                <div className="edit-form">
                  <div className="mb-3">
                    <label className="form-label">Assigned Pandit</label>
                    <Select 
                      options={panditOptions}
                      value={panditOptions.find(o => o.value === editValues.panditId)}
                      onChange={(o) => handleEditChange('panditId', o.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Final Price (₹)</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={editValues.finalPrice} 
                      onChange={(e) => handleEditChange('finalPrice', e.target.value)}
                    />
                  </div>
                  {hasChanges && (
                    <button className="btn btn-success btn-block" onClick={handleUpdateAuditData}>
                      Save Changes
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <h6 className="text-primary mb-2">Customer Details</h6>
                    <p className="mb-1"><strong>Name:</strong> {booking?.user?.name}</p>
                    <p className="mb-1"><strong>Phone:</strong> {booking?.user?.phone}</p>
                    <p className="mb-1 text-lowercase"><strong>Email:</strong> {booking?.user?.email}</p>
                  </div>
                  <div className="mb-4">
                    <h6 className="text-primary mb-2">Service Details</h6>
                    <p className="mb-1"><strong>Puja:</strong> {booking?.service?.title}</p>
                    <p className="mb-1"><strong>Pandit:</strong> {booking?.pandit?.name || "Not Assigned"}</p>
                    <p className="mb-1"><strong>Scheduled:</strong> {convertToIST(booking?.pujaDateTime)}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="col-xl-9 col-lg-8">
          <div className="card">
            <div className="card-header border-0 pb-0">
              <h4 className="card-title">Invoicing Actions</h4>
            </div>
            <div className="card-body">
              <div className="row mb-4">
                <div className="col-md-4 text-center border-end">
                  <span className="text-muted small">Total Amount</span>
                  <h4 className="mb-0">₹{totalBookingAmount}</h4>
                </div>
                <div className="col-md-4 text-center border-end">
                  <span className="text-muted small">Paid to Date</span>
                  <h4 className="mb-0 text-success">₹{totalPaidToDate}</h4>
                </div>
                <div className="col-md-4 text-center">
                  <span className="text-muted small">Balance Due</span>
                  <h4 className="mb-0 text-danger">₹{balanceDue}</h4>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-sm">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Status</th>
                      <th>Actions</th>
                      <th>Versions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Proforma Invoice</td>
                      <td>
                        {booking?.bookingPaymentType === 'full' ? (
                          <span className="text-muted">Not Generated (Full Payment)</span>
                        ) : booking?.proformaInvoiceUrl ? (
                          <span className="text-success">Generated</span>
                        ) : (
                          <span className="text-muted">Pending</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          {booking?.proformaInvoiceUrl && (
                            <a href={booking.proformaInvoiceUrl} target="_blank" rel="noreferrer" className="btn btn-xxs btn-outline-primary">View</a>
                          )}
                          {booking?.bookingPaymentType !== 'full' && (
                            <button className="btn btn-xxs btn-primary" onClick={() => handleRegenerate('proforma')}>
                              {booking?.proformaInvoiceUrl ? 'Regenerate' : 'Generate'}
                            </button>
                          )}
                          {booking?.proformaInvoiceUrl && booking?.bookingPaymentType !== 'full' && (
                            <button className="btn btn-xxs btn-success" onClick={() => handleSendInvoice('proforma')}>Send to Client</button>
                          )}
                        </div>
                      </td>
                      <td>
                        {booking?.proformaInvoiceUrl ? (
                          <button className="btn btn-link btn-xxs p-0 text-primary" onClick={() => handleShowVersions('proforma')}>Show Versions</button>
                        ) : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Tax Invoice (Full)</td>
                      <td>{booking?.finalInvoiceUrl ? <span className="text-success">Generated</span> : <span className="text-muted">Pending</span>}</td>
                      <td>
                        <div className="d-flex gap-2">
                          {booking?.finalInvoiceUrl && <a href={booking.finalInvoiceUrl} target="_blank" rel="noreferrer" className="btn btn-xxs btn-outline-primary">View</a>}
                          <button className="btn btn-xxs btn-primary" onClick={() => handleRegenerate('final')}>
                            {booking?.finalInvoiceUrl ? 'Regenerate' : 'Generate'}
                          </button>
                          {booking?.finalInvoiceUrl && <button className="btn btn-xxs btn-success" onClick={() => handleSendInvoice('final')}>Send to Client</button>}
                        </div>
                      </td>
                      <td>
                        {booking?.finalInvoiceUrl ? (
                          <button className="btn btn-link btn-xxs p-0 text-primary" onClick={() => handleShowVersions('final')}>Show Versions</button>
                        ) : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Pandit Receipt (Partner)</td>
                      <td>
                        {!booking?.panditId ? (
                          <span className="text-danger">No Pandit Assigned</span>
                        ) : booking?.panditInvoiceUrl ? (
                          <span className="text-success">Generated</span>
                        ) : (
                          <span className="text-muted">Pending (Requires Payment)</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          {booking?.panditInvoiceUrl && (
                            <a href={booking.panditInvoiceUrl} target="_blank" rel="noreferrer" className="btn btn-xxs btn-outline-primary">View</a>
                          )}
                          {booking?.panditId && (
                            <button className="btn btn-xxs btn-primary" onClick={() => handleRegenerate('pandit')}>
                              {booking?.panditInvoiceUrl ? 'Regenerate' : 'Generate'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        {booking?.panditInvoiceUrl ? (
                          <button className="btn btn-link btn-xxs p-0 text-primary" onClick={() => handleShowVersions('pandit')}>Show Versions</button>
                        ) : "N/A"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {(booking?.paymentStatus === 'partial' || (balanceDue > 0 && booking?.paymentStatus !== 'done')) && (
                <div className="alert alert-warning d-flex justify-content-between align-items-center py-2 px-3 mb-3">
                  <span className="small mb-0">
                    <i className="fa fa-exclamation-circle me-1"></i>
                    Balance of <strong>₹{balanceDue}</strong> is pending
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-warning"
                    onClick={requestRemainingPayment}
                    disabled={loading}
                  >
                    <i className="fa fa-paper-plane me-1"></i> Send Balance Payment Link
                  </button>
                </div>
              )}

              {/* Record Manual Payment */}
              <div className="card p-3 mb-4" style={{ height: 'auto' }}>
                <h5>Payment Details</h5>
                <p className="text-muted small">Record initial payment if customer has paid during booking creation</p>
                <div className="row">
                  <div className="col-md-4">
                    <label className="form-label">Payment Method</label>
                    <select
                      className="form-select"
                      value={manualPaymentData.paymentMethod}
                      onChange={(e) => setManualPaymentData({ ...manualPaymentData, paymentMethod: e.target.value })}
                    >
                      <option value="">Select payment method</option>
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="upi">UPI</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Amount (₹)</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Enter amount"
                      value={manualPaymentData.amount}
                      onChange={(e) => setManualPaymentData({ ...manualPaymentData, amount: e.target.value })}
                    />
                  </div>
                  {manualPaymentData.paymentMethod === 'upi' && (
                    <div className="col-md-4">
                      <label className="form-label">UPI Payment ID</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter UPI transaction ID"
                        value={manualPaymentData.paymentId}
                        onChange={(e) => setManualPaymentData({ ...manualPaymentData, paymentId: e.target.value })}
                      />
                    </div>
                  )}
                </div>
                {manualPaymentData.paymentMethod && manualPaymentData.amount && (
                  <div className="alert alert-info mt-3 mb-0 d-flex justify-content-between align-items-center">
                    <span><i className="fa fa-info-circle"></i> Payment of ₹{manualPaymentData.amount} via {manualPaymentData.paymentMethod} will be recorded</span>
                    <button type="button" className="btn btn-sm btn-primary ms-3" onClick={handleAddManualPayment} disabled={loading}>
                      Record Payment
                    </button>
                  </div>
                )}
              </div>

              <h6 className="text-primary mt-4 mb-2">Payment Transaction Ledger</h6>
              <div className="table-responsive">
                <table className="table table-striped table-sm">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Stage</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Transaction ID</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {booking?.paymentHistory?.map((p, i) => {
                      const invoiceUrl = p.paymentStage === 'advance' ? booking.proformaInvoiceUrl : booking.finalInvoiceUrl;
                      return (
                        <tr key={i}>
                          <td>{convertToIST(p.createdAt)}</td>
                          <td>
                            <span className={`badge badge-xs ${p.paymentStage === 'advance' ? 'badge-info' : 'badge-success'}`}>
                              {p.paymentStage || "NA"}
                            </span>
                          </td>
                          <td>₹{p.amount}</td>
                          <td>{p.paymentMethod || "NA"}</td>
                          <td><small className="text-muted">{p.transactionId || p.paymentId || "NA"}</small></td>
                          <td><span className={`badge badge-xs ${p.status === 'success' || p.status === 'paid' || p.status === 'captured' ? 'badge-success' : 'badge-warning'}`}>{p.status}</span></td>
                          <td className="text-center">
                            <button
                              type="button"
                              className="btn btn-xs btn-danger p-1"
                              onClick={() => deletePaymentRecord(p.id)}
                              title="Delete transaction record"
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal 
        isOpen={versionModalOpen} 
        onClose={() => setVersionModalOpen(false)}
        title={`Version History: ${selectedVersionType.toUpperCase()}`}
      >
        {versionLoading ? (
          <div className="text-center p-4">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-2 text-primary">Loading versions...</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Version Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {selectedVersions.length > 0 ? selectedVersions.map((v, i) => (
                  <tr key={i}>
                    <td>
                      {convertToIST(v.lastModified)}
                      {v.isLatest && <span className="badge badge-xs badge-success ms-2 text-white">Latest</span>}
                    </td>
                    <td>
                      <a href={v.url} target="_blank" rel="noreferrer" className="btn btn-xxs btn-outline-primary">
                         View
                      </a>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="2" className="text-center">No versions found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}
